/**
 * CashflowCalendar - Calendar view showing entries on each day
 */

import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  isSameDay,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { X, Calendar } from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';
import EntryBadge from './EntryBadge';

const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const CashflowCalendar = () => {
  const { currentMonth, getFilteredEntries, openEditModal } = useCashflowStore();
  const entries = getFilteredEntries();
  const [selectedDay, setSelectedDay] = useState(null); // For day detail modal

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Group entries by date (income first, then by amount descending)
  const entriesByDate = useMemo(() => {
    const map = {};
    entries.forEach(entry => {
      const dateKey = entry.date;
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(entry);
    });
    // Sort each day's entries: income first, then by amount descending
    Object.keys(map).forEach(dateKey => {
      map[dateKey].sort((a, b) => {
        // Income comes first
        if (a.type === 'income' && b.type !== 'income') return -1;
        if (a.type !== 'income' && b.type === 'income') return 1;
        // Then sort by amount descending
        return parseFloat(b.amount) - parseFloat(a.amount);
      });
    });
    return map;
  }, [entries]);

  // Split into weeks
  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-gray-800">
        {WEEKDAYS.map((day, index) => (
          <div
            key={day}
            className={`py-3 text-center text-sm font-medium ${
              index >= 5 ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="divide-y divide-gray-800">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 divide-x divide-gray-800">
            {week.map((day) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayEntries = entriesByDate[dateKey] || [];
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isDayToday = isToday(day);
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;

              return (
                <CalendarDay
                  key={dateKey}
                  day={day}
                  entries={dayEntries}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isDayToday}
                  isWeekend={isWeekend}
                  onEntryClick={openEditModal}
                  onShowMore={() => setSelectedDay({ day, entries: dayEntries })}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Day Detail Modal */}
      <AnimatePresence>
        {selectedDay && (
          <DayDetailModal
            day={selectedDay.day}
            entries={selectedDay.entries}
            onClose={() => setSelectedDay(null)}
            onEntryClick={(entry) => {
              setSelectedDay(null);
              openEditModal(entry);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Currency symbols
const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };

// Day Detail Modal - shows all entries for a specific day
const DayDetailModal = ({ day, entries, onClose, onEntryClick }) => {
  const formattedDate = format(day, 'd MMMM yyyy, EEEE', { locale: tr });

  // Calculate totals by currency
  const totalsByCurrency = entries.reduce((acc, entry) => {
    const amount = parseFloat(entry.amount);
    const currency = entry.currency || 'TRY';

    if (!acc[currency]) {
      acc[currency] = { income: 0, expense: 0 };
    }

    if (entry.type === 'income') {
      acc[currency].income += amount;
    } else {
      acc[currency].expense += amount;
    }
    return acc;
  }, {});

  // Format currency amount
  const formatAmount = (amount, currency) => {
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol}${amount.toLocaleString('tr-TR')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{formattedDate}</h2>
              <p className="text-sm text-gray-400">{entries.length} işlem</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary by currency */}
        <div className="flex flex-wrap gap-3 px-4 py-3 border-b border-gray-800 bg-gray-800/30">
          {Object.entries(totalsByCurrency).map(([currency, totals]) => (
            <div key={currency} className="flex items-center gap-2">
              {totals.income > 0 && (
                <span className="text-sm text-emerald-400">
                  +{formatAmount(totals.income, currency)}
                </span>
              )}
              {totals.income > 0 && totals.expense > 0 && (
                <span className="text-gray-600">/</span>
              )}
              {totals.expense > 0 && (
                <span className="text-sm text-red-400">
                  -{formatAmount(totals.expense, currency)}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Entries list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {entries.map((entry) => (
            <EntryBadge
              key={entry.id}
              entry={entry}
              onClick={() => onEntryClick(entry)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Calendar day cell component
const CalendarDay = ({ day, entries, isCurrentMonth, isToday, isWeekend, onEntryClick, onShowMore }) => {
  const dayNumber = format(day, 'd');

  // Calculate totals for the day
  const dayTotal = entries.reduce(
    (acc, entry) => {
      const amount = parseFloat(entry.amount);
      if (entry.type === 'income') {
        acc.income += amount;
      } else {
        acc.expense += amount;
      }
      return acc;
    },
    { income: 0, expense: 0 }
  );

  // Limit visible entries
  const visibleEntries = entries.slice(0, 3);
  const hiddenCount = entries.length - 3;

  return (
    <motion.div
      className={`min-h-[120px] p-2 transition-colors ${
        isCurrentMonth
          ? isToday
            ? 'bg-indigo-900/20'
            : isWeekend
            ? 'bg-gray-900/30'
            : 'bg-transparent'
          : 'bg-gray-950/50'
      } hover:bg-gray-800/30`}
      initial={false}
    >
      {/* Day number */}
      <div className="flex items-center justify-between mb-1">
        <span
          className={`text-sm font-medium ${
            isCurrentMonth
              ? isToday
                ? 'text-indigo-400'
                : isWeekend
                ? 'text-gray-500'
                : 'text-gray-300'
              : 'text-gray-600'
          } ${
            isToday
              ? 'w-7 h-7 bg-indigo-500 text-white rounded-full flex items-center justify-center'
              : ''
          }`}
        >
          {dayNumber}
        </span>

        {/* Day totals indicator */}
        {entries.length > 0 && (
          <div className="flex gap-1">
            {dayTotal.income > 0 && (
              <span className="w-2 h-2 bg-emerald-500 rounded-full" title={`+${dayTotal.income}`} />
            )}
            {dayTotal.expense > 0 && (
              <span className="w-2 h-2 bg-red-500 rounded-full" title={`-${dayTotal.expense}`} />
            )}
          </div>
        )}
      </div>

      {/* Entries */}
      <div className="space-y-1">
        {visibleEntries.map((entry) => (
          <EntryBadge
            key={entry.id}
            entry={entry}
            onClick={() => onEntryClick(entry)}
          />
        ))}

        {/* More indicator */}
        {hiddenCount > 0 && (
          <button
            className="w-full text-xs text-gray-500 hover:text-gray-300 py-1 transition-colors"
            onClick={onShowMore}
          >
            +{hiddenCount} daha
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default CashflowCalendar;
